/**
 * Les lignes de la liste « Premiers pas » (C21 SP4, 4.2), dérivées de l'état
 * serveur par une fonction pure. Deux paliers (C24 SP3, décision 6) : les six
 * premières lignes (cinq sans note « Retours ») ; puis, quand les cinq
 * premières sont faites, « Pour aller plus loin », deux lignes (relier un
 * passage à une autre note, lire ce qu'un ami entend par un mot). Une marche
 * à la fois : le palier 2 n'est rendu qu'une fois le palier 1 fini. Les
 * libellés sont ceux de l'écran, mot pour mot ; aucun terme technique. La
 * liste fait faire : chaque ligne porte un bouton, pas un paragraphe.
 */
import type { PremiersPasEtat } from '../../../services/premiersPasAPI';

export type LigneId = 'document' | 'citation' | 'concept' | 'assistant' | 'partage' | 'retours' | 'noteReliee' | 'definition';
export type Palier = 1 | 2;

export interface LignePremiersPas {
  id: LigneId;
  numero: number;
  palier: Palier;
  titre: string;
  phrase: string;
  fait: boolean;
  /** Libellé du bouton qui fait le geste ; `null` quand la ligne n'en a pas ici. */
  bouton: string | null;
  /** Le geste se fait sur ordinateur (dit à l'écran sur mobile). */
  ordinateurSeulement: boolean;
}

export const LIBELLES_PREMIERS_PAS: Record<LigneId, string> = {
  document: 'Ajouter votre document',
  citation: 'Garder une citation',
  concept: 'Écrire une note et la relier à un concept',
  assistant: 'Demander à l’assistant',
  partage: 'Partager',
  retours: 'Dire ce qui vous a gêné',
  noteReliee: 'Relier un passage à une autre note',
  definition: 'Lire ce qu’un ami entend par un mot',
};

/** Titres du bandeau selon le palier rendu (C24 SP3). */
export const TITRES_BANDEAU = { 1: 'Premiers pas', 2: 'Pour aller plus loin' } as const;

const PHRASES: Record<LigneId, string> = {
  document: 'Un PDF, un EPUB, une page web enregistrée : ce que vous lisez en ce moment.',
  citation: 'Dans le document, sélectionnez un passage puis « Citer » : la note garde la page.',
  concept: 'Quelques lignes à vous, puis le concept qui compte, relié depuis l’éditeur.',
  assistant: 'Posez une question sur ce que vous avez lu : la réponse cite vos notes.',
  partage: 'Une note en page publique, ou envoyée à un contact.',
  retours: 'Une ligne dans votre note « Retours », à chaque gêne, même petite.',
  noteReliee: 'Dans une note, sélectionnez une phrase, puis « Note » : la citation qu’elle commente, la note qu’elle contredit.',
  definition: 'Dans une note partagée, un mot souligné s’ouvre sur « Ce que Claire entend par… ».',
};

/** Ligne 8 tant qu'aucun contact n'a rien partagé : la phrase le dit, le bouton mène aux Contacts. */
const DEFINITION_SANS_PARTAGE = 'Quand un contact vous aura partagé une note, ses mots soulignés s’ouvrent sur ce qu’il entend par là.';

/** Le palier à rendre : 2 dès que les cinq premières lignes sont faites (la ligne « Retours » ne retient pas). */
export function palierCourant(etat: PremiersPasEtat): Palier {
  return etat.document && etat.citation && etat.concept && etat.assistant && etat.partage ? 2 : 1;
}

export function lignesPremiersPas(etat: PremiersPasEtat, options: { retoursOuvert: boolean; mobile: boolean }): LignePremiersPas[] {
  const { mobile } = options;
  if (palierCourant(etat) === 2) return lignesDuPalier2(etat, mobile);
  const lignes: LignePremiersPas[] = [
    { id: 'document', numero: 1, palier: 1, titre: LIBELLES_PREMIERS_PAS.document, phrase: mobile ? 'Sur ordinateur : un PDF, un EPUB, une page web enregistrée.' : PHRASES.document, fait: etat.document, bouton: mobile ? null : 'Choisir un fichier', ordinateurSeulement: mobile },
    { id: 'citation', numero: 2, palier: 1, titre: LIBELLES_PREMIERS_PAS.citation, phrase: PHRASES.citation, fait: etat.citation, bouton: 'Ouvrir un document', ordinateurSeulement: false },
    { id: 'concept', numero: 3, palier: 1, titre: LIBELLES_PREMIERS_PAS.concept, phrase: PHRASES.concept, fait: etat.concept, bouton: 'Écrire une note', ordinateurSeulement: false },
    { id: 'assistant', numero: 4, palier: 1, titre: LIBELLES_PREMIERS_PAS.assistant, phrase: PHRASES.assistant, fait: etat.assistant, bouton: 'Poser une question', ordinateurSeulement: false },
    { id: 'partage', numero: 5, palier: 1, titre: LIBELLES_PREMIERS_PAS.partage, phrase: PHRASES.partage, fait: etat.partage, bouton: 'Partager ma dernière note', ordinateurSeulement: false },
  ];
  // La ligne 6 n'existe que pour qui a reçu sa note « Retours — Prénom »
  // (bêta amis, D4). Sans elle (le propriétaire du dossier « Retours », toute
  // personne hors bêta), elle n'était cochable nulle part : la liste ne se
  // terminait jamais et revenait sur chaque navigateur (retour du 16/10/2026).
  if (!etat.retours) return lignes;
  // Sur mobile, la note « Retours » s'ouvre aussi (fiche en lecture, « Partagée
  // avec vous ») : la ligne 6 se coche à cette première ouverture, comme sur le
  // bureau — sinon, un téléphone ne pouvait jamais la cocher et la carte restait
  // affichée à demeure (retour du 06/10/2026). Écrire reste un geste du bureau.
  const retoursPhrase = mobile ? 'Une ligne à chaque gêne, même petite. Votre note « Retours » se lit ici et s’écrit sur ordinateur.' : PHRASES.retours;
  const retoursFaite = etat.retours.modifiee || options.retoursOuvert;
  return [
    ...lignes,
    { id: 'retours', numero: 6, palier: 1, titre: LIBELLES_PREMIERS_PAS.retours, phrase: retoursPhrase, fait: retoursFaite, bouton: 'Ouvrir ma note « Retours »', ordinateurSeulement: false },
  ];
}

/**
 * « Pour aller plus loin » (C24 SP3, décision 6). Ligne 8 : sans partage reçu,
 * une phrase honnête et « Ajouter un contact » ; avec, « Voir mes actualités ».
 * Sur le téléphone, qui n'a ni Contacts ni fil « Actualités », la ligne renvoie
 * à l'ordinateur tant qu'aucun partage n'est reçu, et à la liste des notes après.
 */
function lignesDuPalier2(etat: PremiersPasEtat, mobile: boolean): LignePremiersPas[] {
  const definition: LignePremiersPas = etat.partageRecu
    ? { id: 'definition', numero: 8, palier: 2, titre: LIBELLES_PREMIERS_PAS.definition, phrase: PHRASES.definition, fait: etat.definitionLue, bouton: mobile ? 'Voir mes notes' : 'Voir mes actualités', ordinateurSeulement: false }
    : { id: 'definition', numero: 8, palier: 2, titre: LIBELLES_PREMIERS_PAS.definition, phrase: mobile ? `${DEFINITION_SANS_PARTAGE} Les contacts s’ajoutent sur ordinateur.` : DEFINITION_SANS_PARTAGE, fait: etat.definitionLue, bouton: mobile ? null : 'Ajouter un contact', ordinateurSeulement: mobile };
  return [
    { id: 'noteReliee', numero: 7, palier: 2, titre: LIBELLES_PREMIERS_PAS.noteReliee, phrase: PHRASES.noteReliee, fait: etat.noteReliee, bouton: 'Ouvrir ma dernière note', ordinateurSeulement: false },
    definition,
  ];
}

/** Le titre du bandeau : celui du palier des lignes rendues. */
export function titreDuBandeau(lignes: LignePremiersPas[]): string {
  return TITRES_BANDEAU[lignes[0]?.palier ?? 1];
}

export function toutesFaites(lignes: LignePremiersPas[]): boolean {
  return lignes.every(l => l.fait);
}
